import type { BodyNodeLink } from '../types';
import type { JointAngles, JointDefinition, JointKey } from './JointAngles';
import type { SkeletonDefinition } from './PoseEngine';

export type AppMode = 'pose' | 'tracking';

export interface Settings {
  appMode: AppMode;
  mirror: boolean;
  showLabels: boolean;
  showAngles: boolean;
  colorizeByScore: boolean;
  minScore: number;
  inferEveryNthFrame: number;
  numPoses: number;
  primaryIndex: number;
  showBoxes: boolean;
  showTrails: boolean;
  showGhosts: boolean;
  showTeams: boolean;
  trailLength: number;
  ghostMaxAgeMs: number;
  reidLifetimeMs: number;
  showHeatmap: boolean;
  heatmapOpacity: number;
  heatmapOnCourt: boolean;
  useDeepReid: boolean;
  useWorker: boolean;
  smoothPose: boolean;
  poseModelQuality: 'light' | 'heavy';
  useCropPipeline: boolean;
  detectorKind: 'coco' | 'yolo';
  deepReidKind: 'mediapipe' | 'onnx';
  filterBackend: 'alphabeta' | 'kalman';
  showEdges: boolean;
  bundleOnRecord: boolean;
  personsOnly: boolean;
}

export interface HudMetrics {
  fps: number;
  inferenceTimeMs: number;
  activeNodes: number;
  totalNodes: number;
  resolution: { width: number; height: number };
}

const HISTOGRAM_SIZE = 60;
const HISTOGRAM_MAX_MS = 60;
const ANGLE_HISTORY = 60;
const SVG_NS = 'http://www.w3.org/2000/svg';

export class HudController {
  private readonly inferSamples: number[] = [];
  private readonly histogramBars: HTMLDivElement[] = [];
  private nodeBars: HTMLDivElement[] = [];
  private angleHistory = new Map<JointKey, (number | null)[]>();
  private angleValueEls = new Map<JointKey, HTMLElement>();
  private anglePolylines = new Map<JointKey, SVGPolylineElement>();
  private settingsListeners: ((s: Settings) => void)[] = [];
  private nodeNames: readonly string[];
  private jointDefs: readonly JointDefinition[];

  constructor(
    private readonly root: HTMLElement,
    skeleton: SkeletonDefinition,
    private settings: Settings,
  ) {
    this.nodeNames = skeleton.nodeNames;
    this.jointDefs = skeleton.jointDefs;
    this.wireInputs();
    this.buildHistogram();
    this.buildNodeScoreBars();
    this.buildAngleRows();
    this.applyAppModeVisibility(this.settings.appMode);
  }

  setAppMode(mode: AppMode): void {
    if (this.settings.appMode === mode) return;
    this.settings = { ...this.settings, appMode: mode };
    this.applyAppModeVisibility(mode);
    const radio = this.root.querySelector<HTMLInputElement>(
      `input[name="appmode"][value="${mode}"]`,
    );
    if (radio) radio.checked = true;
  }

  setTrackingStats(detections: number, tracks: number): void {
    const d = this.root.querySelector<HTMLElement>('#hud-detections');
    const t = this.root.querySelector<HTMLElement>('#hud-tracks');
    if (d) d.textContent = String(detections);
    if (t) t.textContent = String(tracks);
  }

  setGoneCount(n: number): void {
    const el = this.root.querySelector<HTMLElement>('#hud-gone');
    if (el) el.textContent = String(n);
  }

  setReidMode(mode: 'shallow' | 'deep' | 'loading deep'): void {
    const el = this.root.querySelector<HTMLElement>('#hud-reidmode');
    if (el) el.textContent = mode;
  }

  private applyAppModeVisibility(mode: AppMode): void {
    const elements = this.root.querySelectorAll<HTMLElement>('[data-mode]');
    for (const el of elements) {
      const target = el.dataset.mode as AppMode;
      el.hidden = target !== mode;
    }
  }

  getSettings(): Readonly<Settings> {
    return this.settings;
  }

  onSettingsChange(cb: (s: Settings) => void): void {
    this.settingsListeners.push(cb);
  }

  setStatus(text: string): void {
    this.root.querySelector<HTMLElement>('#hud-status')!.textContent = text;
  }

  updateMetrics(m: HudMetrics): void {
    this.root.querySelector<HTMLElement>('#hud-fps')!.textContent = String(m.fps);
    this.root.querySelector<HTMLElement>('#hud-infer')!.textContent =
      `${m.inferenceTimeMs.toFixed(1)} ms`;
    this.root.querySelector<HTMLElement>('#hud-nodes')!.textContent =
      `${m.activeNodes} / ${m.totalNodes}`;
    this.root.querySelector<HTMLElement>('#hud-res')!.textContent =
      `${m.resolution.width}×${m.resolution.height}`;
  }

  pushInferSample(ms: number): void {
    this.inferSamples.push(ms);
    if (this.inferSamples.length > HISTOGRAM_SIZE) this.inferSamples.shift();
    this.renderHistogram();
  }

  updateNodeScores(bnl: BodyNodeLink): void {
    for (let i = 0; i < this.nodeBars.length; i += 1) {
      const node = bnl.nodes[i];
      const bar = this.nodeBars[i];
      const score = node?.score ?? 0;
      bar.style.setProperty('--v', String(score));
      bar.dataset.score = score.toFixed(2);
    }
  }

  rebuildSkeleton(skeleton: SkeletonDefinition): void {
    this.nodeNames = skeleton.nodeNames;
    this.jointDefs = skeleton.jointDefs;
    this.nodeBars = [];
    this.angleHistory = new Map();
    this.angleValueEls = new Map();
    this.anglePolylines = new Map();
    const nodebars = this.root.querySelector<HTMLElement>('#hud-nodebars');
    if (nodebars) nodebars.textContent = '';
    const angles = this.root.querySelector<HTMLElement>('#hud-angles');
    if (angles) angles.textContent = '';
    this.buildNodeScoreBars();
    this.buildAngleRows();
  }

  setMultiPoseEnabled(enabled: boolean): void {
    const people = this.root.querySelector<HTMLInputElement>('#slider-people');
    const primary = this.root.querySelector<HTMLInputElement>('#slider-primary');
    if (!people || !primary) return;
    if (!enabled) {
      people.value = '1';
      people.disabled = true;
      primary.value = '0';
      primary.max = '0';
      primary.disabled = true;
      this.settings = { ...this.settings, numPoses: 1, primaryIndex: 0 };
      const peopleVal = this.root.querySelector<HTMLElement>('#slider-people-val');
      const primaryVal = this.root.querySelector<HTMLElement>('#slider-primary-val');
      if (peopleVal) peopleVal.textContent = '1';
      if (primaryVal) primaryVal.textContent = '0';
    } else {
      people.disabled = false;
    }
  }

  private wireInputs(): void {
    const mirror = this.root.querySelector<HTMLInputElement>('#toggle-mirror')!;
    const labels = this.root.querySelector<HTMLInputElement>('#toggle-labels')!;
    const angles = this.root.querySelector<HTMLInputElement>('#toggle-angles')!;
    const colorize = this.root.querySelector<HTMLInputElement>('#toggle-colorize')!;
    const minScore = this.root.querySelector<HTMLInputElement>('#slider-minscore')!;
    const minScoreVal = this.root.querySelector<HTMLElement>('#slider-minscore-val')!;
    const skip = this.root.querySelector<HTMLInputElement>('#slider-skip')!;
    const skipVal = this.root.querySelector<HTMLElement>('#slider-skip-val')!;
    const people = this.root.querySelector<HTMLInputElement>('#slider-people')!;
    const peopleVal = this.root.querySelector<HTMLElement>('#slider-people-val')!;
    const primary = this.root.querySelector<HTMLInputElement>('#slider-primary')!;
    const primaryVal = this.root.querySelector<HTMLElement>('#slider-primary-val')!;
    const appmodeRadios = this.root.querySelectorAll<HTMLInputElement>('input[name="appmode"]');
    const boxes = this.root.querySelector<HTMLInputElement>('#toggle-boxes')!;
    const trails = this.root.querySelector<HTMLInputElement>('#toggle-trails')!;
    const trail = this.root.querySelector<HTMLInputElement>('#slider-trail')!;
    const trailVal = this.root.querySelector<HTMLElement>('#slider-trail-val')!;
    const ghosts = this.root.querySelector<HTMLInputElement>('#toggle-ghosts')!;
    const ghost = this.root.querySelector<HTMLInputElement>('#slider-ghost')!;
    const ghostVal = this.root.querySelector<HTMLElement>('#slider-ghost-val')!;
    const teams = this.root.querySelector<HTMLInputElement>('#toggle-teams')!;
    const reid = this.root.querySelector<HTMLInputElement>('#slider-reid')!;
    const reidVal = this.root.querySelector<HTMLElement>('#slider-reid-val')!;
    const heatmap = this.root.querySelector<HTMLInputElement>('#toggle-heatmap')!;
    const heatmapAlpha = this.root.querySelector<HTMLInputElement>('#slider-heatmap')!;
    const heatmapAlphaVal = this.root.querySelector<HTMLElement>('#slider-heatmap-val')!;
    const deepReid = this.root.querySelector<HTMLInputElement>('#toggle-deepreid')!;
    const workerToggle = this.root.querySelector<HTMLInputElement>('#toggle-worker')!;
    const smooth = this.root.querySelector<HTMLInputElement>('#toggle-smooth')!;
    const qualityRadios = this.root.querySelectorAll<HTMLInputElement>('input[name="quality"]');
    const cropPose = this.root.querySelector<HTMLInputElement>('#toggle-crop-pose')!;
    const detectorRadios = this.root.querySelectorAll<HTMLInputElement>('input[name="detector"]');
    const deepReidRadios = this.root.querySelectorAll<HTMLInputElement>(
      'input[name="deepreidkind"]',
    );
    const filterRadios = this.root.querySelectorAll<HTMLInputElement>('input[name="filter"]');
    const edges = this.root.querySelector<HTMLInputElement>('#toggle-edges')!;
    const heatCourt = this.root.querySelector<HTMLInputElement>('#toggle-heat-court')!;
    const bundle = this.root.querySelector<HTMLInputElement>('#toggle-bundle-record')!;
    const personsOnly = this.root.querySelector<HTMLInputElement>('#toggle-persons-only')!;

    mirror.checked = this.settings.mirror;
    labels.checked = this.settings.showLabels;
    angles.checked = this.settings.showAngles;
    colorize.checked = this.settings.colorizeByScore;
    minScore.value = String(this.settings.minScore);
    minScoreVal.textContent = this.settings.minScore.toFixed(2);
    skip.value = String(this.settings.inferEveryNthFrame);
    skipVal.textContent = String(this.settings.inferEveryNthFrame);
    people.value = String(this.settings.numPoses);
    peopleVal.textContent = String(this.settings.numPoses);
    primary.max = String(Math.max(0, this.settings.numPoses - 1));
    primary.value = String(this.settings.primaryIndex);
    primary.disabled = this.settings.numPoses <= 1;
    primaryVal.textContent = String(this.settings.primaryIndex);
    boxes.checked = this.settings.showBoxes;
    trails.checked = this.settings.showTrails;
    trail.value = String(this.settings.trailLength);
    trailVal.textContent = String(this.settings.trailLength);
    ghosts.checked = this.settings.showGhosts;
    ghost.value = String(this.settings.ghostMaxAgeMs);
    ghostVal.textContent = `${(this.settings.ghostMaxAgeMs / 1000).toFixed(1)}s`;
    teams.checked = this.settings.showTeams;
    reid.value = String(this.settings.reidLifetimeMs);
    reidVal.textContent = `${Math.round(this.settings.reidLifetimeMs / 1000)}s`;
    heatmap.checked = this.settings.showHeatmap;
    heatmapAlpha.value = String(this.settings.heatmapOpacity);
    heatmapAlphaVal.textContent = this.settings.heatmapOpacity.toFixed(2);
    deepReid.checked = this.settings.useDeepReid;
    workerToggle.checked = this.settings.useWorker;
    smooth.checked = this.settings.smoothPose;
    for (const r of qualityRadios) r.checked = r.value === this.settings.poseModelQuality;
    cropPose.checked = this.settings.useCropPipeline;
    for (const r of detectorRadios) r.checked = r.value === this.settings.detectorKind;
    for (const r of deepReidRadios) r.checked = r.value === this.settings.deepReidKind;
    for (const r of filterRadios) r.checked = r.value === this.settings.filterBackend;
    edges.checked = this.settings.showEdges;
    heatCourt.checked = this.settings.heatmapOnCourt;
    bundle.checked = this.settings.bundleOnRecord;
    personsOnly.checked = this.settings.personsOnly;
    for (const r of appmodeRadios) {
      r.checked = r.value === this.settings.appMode;
    }

    const emit = (): void => {
      for (const cb of this.settingsListeners) cb(this.settings);
    };

    mirror.addEventListener('change', () => {
      this.settings = { ...this.settings, mirror: mirror.checked };
      emit();
    });
    labels.addEventListener('change', () => {
      this.settings = { ...this.settings, showLabels: labels.checked };
      emit();
    });
    angles.addEventListener('change', () => {
      this.settings = { ...this.settings, showAngles: angles.checked };
      emit();
    });
    colorize.addEventListener('change', () => {
      this.settings = { ...this.settings, colorizeByScore: colorize.checked };
      emit();
    });
    minScore.addEventListener('input', () => {
      const v = Number(minScore.value);
      this.settings = { ...this.settings, minScore: v };
      minScoreVal.textContent = v.toFixed(2);
      emit();
    });
    skip.addEventListener('input', () => {
      const v = Math.max(1, Math.floor(Number(skip.value)));
      this.settings = { ...this.settings, inferEveryNthFrame: v };
      skipVal.textContent = String(v);
      emit();
    });
    people.addEventListener('input', () => {
      const v = Math.max(1, Math.min(5, Math.floor(Number(people.value))));
      const nextPrimary = Math.min(this.settings.primaryIndex, v - 1);
      this.settings = { ...this.settings, numPoses: v, primaryIndex: nextPrimary };
      peopleVal.textContent = String(v);
      primary.max = String(Math.max(0, v - 1));
      primary.value = String(nextPrimary);
      primary.disabled = v <= 1;
      primaryVal.textContent = String(nextPrimary);
      emit();
    });
    primary.addEventListener('input', () => {
      const v = Math.max(
        0,
        Math.min(this.settings.numPoses - 1, Math.floor(Number(primary.value))),
      );
      this.settings = { ...this.settings, primaryIndex: v };
      primaryVal.textContent = String(v);
      emit();
    });
    boxes.addEventListener('change', () => {
      this.settings = { ...this.settings, showBoxes: boxes.checked };
      emit();
    });
    trails.addEventListener('change', () => {
      this.settings = { ...this.settings, showTrails: trails.checked };
      emit();
    });
    trail.addEventListener('input', () => {
      const v = Math.max(10, Math.min(300, Math.floor(Number(trail.value))));
      this.settings = { ...this.settings, trailLength: v };
      trailVal.textContent = String(v);
      emit();
    });
    ghosts.addEventListener('change', () => {
      this.settings = { ...this.settings, showGhosts: ghosts.checked };
      emit();
    });
    ghost.addEventListener('input', () => {
      const v = Math.max(500, Math.min(5000, Math.floor(Number(ghost.value))));
      this.settings = { ...this.settings, ghostMaxAgeMs: v };
      ghostVal.textContent = `${(v / 1000).toFixed(1)}s`;
      emit();
    });
    teams.addEventListener('change', () => {
      this.settings = { ...this.settings, showTeams: teams.checked };
      emit();
    });
    reid.addEventListener('input', () => {
      const v = Math.max(0, Math.min(30000, Math.floor(Number(reid.value))));
      this.settings = { ...this.settings, reidLifetimeMs: v };
      reidVal.textContent = `${Math.round(v / 1000)}s`;
      emit();
    });
    heatmap.addEventListener('change', () => {
      this.settings = { ...this.settings, showHeatmap: heatmap.checked };
      emit();
    });
    heatmapAlpha.addEventListener('input', () => {
      const v = Math.max(0, Math.min(1, Number(heatmapAlpha.value)));
      this.settings = { ...this.settings, heatmapOpacity: v };
      heatmapAlphaVal.textContent = v.toFixed(2);
      emit();
    });
    deepReid.addEventListener('change', () => {
      this.settings = { ...this.settings, useDeepReid: deepReid.checked };
      emit();
    });
    workerToggle.addEventListener('change', () => {
      this.settings = { ...this.settings, useWorker: workerToggle.checked };
      emit();
    });
    smooth.addEventListener('change', () => {
      this.settings = { ...this.settings, smoothPose: smooth.checked };
      emit();
    });
    for (const r of qualityRadios) {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.settings = {
          ...this.settings,
          poseModelQuality: r.value as 'light' | 'heavy',
        };
        emit();
      });
    }
    cropPose.addEventListener('change', () => {
      this.settings = { ...this.settings, useCropPipeline: cropPose.checked };
      emit();
    });
    for (const r of detectorRadios) {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.settings = {
          ...this.settings,
          detectorKind: r.value as 'coco' | 'yolo',
        };
        emit();
      });
    }
    for (const r of deepReidRadios) {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.settings = {
          ...this.settings,
          deepReidKind: r.value as 'mediapipe' | 'onnx',
        };
        emit();
      });
    }
    for (const r of filterRadios) {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.settings = {
          ...this.settings,
          filterBackend: r.value as 'alphabeta' | 'kalman',
        };
        emit();
      });
    }
    edges.addEventListener('change', () => {
      this.settings = { ...this.settings, showEdges: edges.checked };
      emit();
    });
    heatCourt.addEventListener('change', () => {
      this.settings = { ...this.settings, heatmapOnCourt: heatCourt.checked };
      emit();
    });
    bundle.addEventListener('change', () => {
      this.settings = { ...this.settings, bundleOnRecord: bundle.checked };
      emit();
    });
    personsOnly.addEventListener('change', () => {
      this.settings = { ...this.settings, personsOnly: personsOnly.checked };
      emit();
    });
    for (const r of appmodeRadios) {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.settings = { ...this.settings, appMode: r.value as AppMode };
        this.applyAppModeVisibility(this.settings.appMode);
        emit();
      });
    }
  }

  private buildHistogram(): void {
    const container = this.root.querySelector<HTMLElement>('#hud-histogram')!;
    for (let i = 0; i < HISTOGRAM_SIZE; i += 1) {
      const bar = document.createElement('div');
      bar.className = 'hist-bar';
      bar.style.setProperty('--h', '0');
      container.appendChild(bar);
      this.histogramBars.push(bar);
    }
  }

  private renderHistogram(): void {
    const offset = HISTOGRAM_SIZE - this.inferSamples.length;
    for (let i = 0; i < HISTOGRAM_SIZE; i += 1) {
      const sampleIdx = i - offset;
      const ms = sampleIdx >= 0 ? (this.inferSamples[sampleIdx] ?? 0) : 0;
      const ratio = Math.min(1, ms / HISTOGRAM_MAX_MS);
      this.histogramBars[i].style.setProperty('--h', ratio.toFixed(3));
      this.histogramBars[i].dataset.ms = ms.toFixed(1);
    }
  }

  private buildNodeScoreBars(): void {
    const container = this.root.querySelector<HTMLElement>('#hud-nodebars')!;
    const cols = Math.max(6, Math.min(11, Math.ceil(Math.sqrt(this.nodeNames.length * 2))));
    container.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
    for (let i = 0; i < this.nodeNames.length; i += 1) {
      const bar = document.createElement('div');
      bar.className = 'node-bar';
      bar.title = this.nodeNames[i];
      bar.style.setProperty('--v', '0');
      container.appendChild(bar);
      this.nodeBars.push(bar);
    }
  }

  private buildAngleRows(): void {
    const container = this.root.querySelector<HTMLElement>('#hud-angles')!;
    for (const def of this.jointDefs) {
      this.angleHistory.set(def.key, []);

      const row = document.createElement('div');
      row.className = 'angle-row';

      const label = document.createElement('span');
      label.className = 'angle-label';
      label.style.color = def.color;
      const dot = document.createElement('span');
      dot.className = 'angle-dot';
      label.appendChild(dot);
      label.appendChild(document.createTextNode(def.label));
      row.appendChild(label);

      const value = document.createElement('span');
      value.className = 'angle-value stale';
      value.textContent = '--';
      row.appendChild(value);
      this.angleValueEls.set(def.key, value);

      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'angle-spark');
      svg.setAttribute('viewBox', `0 0 ${ANGLE_HISTORY - 1} 180`);
      svg.setAttribute('preserveAspectRatio', 'none');
      const poly = document.createElementNS(SVG_NS, 'polyline');
      poly.setAttribute('fill', 'none');
      poly.setAttribute('stroke', def.color);
      poly.setAttribute('stroke-width', '1.5');
      poly.setAttribute('vector-effect', 'non-scaling-stroke');
      svg.appendChild(poly);
      row.appendChild(svg);
      this.anglePolylines.set(def.key, poly);

      container.appendChild(row);
    }
  }

  updateAngles(angles: JointAngles): void {
    for (const def of this.jointDefs) {
      const value = angles[def.key];
      const history = this.angleHistory.get(def.key);
      if (!history) continue;
      history.push(value ?? null);
      if (history.length > ANGLE_HISTORY) history.shift();

      const valueEl = this.angleValueEls.get(def.key);
      if (valueEl) {
        if (typeof value === 'number') {
          valueEl.textContent = `${value.toFixed(0)}°`;
          valueEl.classList.remove('stale');
        } else {
          valueEl.textContent = '--';
          valueEl.classList.add('stale');
        }
      }

      const poly = this.anglePolylines.get(def.key);
      if (poly) poly.setAttribute('points', serializePolyline(history));
    }
  }
}

function serializePolyline(history: (number | null)[]): string {
  const parts: string[] = [];
  for (let i = 0; i < history.length; i += 1) {
    const v = history[i];
    if (v === null || !Number.isFinite(v)) continue;
    const y = 180 - Math.max(0, Math.min(180, v));
    parts.push(`${i},${y.toFixed(1)}`);
  }
  return parts.join(' ');
}
